import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { MarcadorAutoanticorpo } from "../../../types";
import { useAuth } from "../../auth/context/AuthContext";
import { db } from "../../../lib/firebase";
import {
  registrarEdicaoTrilha,
  registrarExclusaoTrilha,
} from "../../../shared/utils/trilhaAuditoria";
import { calcularCamposAlterados } from "../../../shared/utils/trilhaAuditoriaDiff";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

interface MarcadoresContextProps {
  marcadores: MarcadorAutoanticorpo[];
  loading: boolean;
  adicionarMarcador: (
    pacienteId: string,
    dados: Omit<
      MarcadorAutoanticorpo,
      "id" | "registradoPor" | "registradoPorNome"
    >
  ) => Promise<void>;
  listarMarcadoresPorPaciente: (pacienteId: string) => MarcadorAutoanticorpo[];
  editarMarcador: (
    id: string,
    dados: Partial<MarcadorAutoanticorpo>
  ) => Promise<void>;
  removerMarcador: (id: string) => Promise<void>;
  carregarMarcadores: () => Promise<void>;
}

const MarcadoresContext = createContext<MarcadoresContextProps | undefined>(
  undefined
);

export function MarcadoresProvider({ children }: { children: ReactNode }) {
  console.log("🔥 MarcadoresProvider montado!"); // ← ADICIONA ESTA LINHA
  const { usuarioLogado } = useAuth();
  const [marcadores, setMarcadores] = useState<MarcadorAutoanticorpo[]>([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // 📥 CARREGAR MARCADORES DO FIREBASE
  // ============================================
  const carregarMarcadores = async () => {
    try {
      setLoading(true);
      console.log("🔍 Carregando marcadores do Firebase...");

      const querySnapshot = await getDocs(collection(db, "marcadores"));

      const marcadoresFormatados: MarcadorAutoanticorpo[] =
        querySnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            pacienteId: data.pacienteId,
            dataColeta:
              data.dataColeta?.toDate?.()?.toISOString?.() || data.dataColeta,
            afp: data.afp,
            cea: data.cea,
            ca199: data.ca199,
            ca125: data.ca125,
            ferro: data.ferro,
            ferritina: data.ferritina,
            saturacaoTransferrina: data.saturacaoTransferrina,
            antiLKM: data.antiLKM,
            antiMusculoLiso: data.antiMusculoLiso,
            antiMitocondria: data.antiMitocondria,
            fan: data.fan,
            observacoes: data.observacoes,
            registradoPor: data.registradoPor,
            registradoPorNome: data.registradoPorNome,
            editadoPor: data.editadoPor,
            editadoPorNome: data.editadoPorNome,
            dataEdicao:
              data.dataEdicao?.toDate?.()?.toISOString?.() || data.dataEdicao,
            criadoEm:
              data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
          } as MarcadorAutoanticorpo;
        });

      setMarcadores(marcadoresFormatados);
      console.log("✅ Marcadores carregados:", marcadoresFormatados.length);
    } catch (error) {
      console.error("❌ Erro ao carregar marcadores:", error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🔄 CARREGAR AO MONTAR
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) {
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log("🔍 Escutando marcadores em tempo real...");

    const unsubscribe = onSnapshot(
      collection(db, "marcadores"),
      (snapshot) => {
        const lista: MarcadorAutoanticorpo[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            criadoEm:
              data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
          } as MarcadorAutoanticorpo;
        });

        setMarcadores(lista);
        setLoading(false);
        console.log("✅ Marcadores atualizados (tempo real):", lista.length);
      },
      (error) => {
        console.error("❌ Erro no listener de marcadores:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // ➕ ADICIONAR MARCADOR
  // ============================================
  const adicionarMarcador = async (
    pacienteId: string,
    dados: Omit<
      MarcadorAutoanticorpo,
      "id" | "registradoPor" | "registradoPorNome"
    >
  ) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Adicionando marcador...");

      // Remove campos undefined
      const dadosLimpos = Object.fromEntries(
        Object.entries(dados).filter(([_, value]) => value !== undefined)
      );

      await addDoc(collection(db, "marcadores"), {
        pacienteId,
        registradoPor: usuarioLogado.id,
        registradoPorNome: usuarioLogado.nome,
        ...dadosLimpos,
        criadoEm: serverTimestamp(),
      });

      console.log("✅ Marcador adicionado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao adicionar marcador:", error);
      throw error;
    }
  };

  // ============================================
  // 📋 LISTAR MARCADORES DE UM PACIENTE
  // ============================================
  const listarMarcadoresPorPaciente = (
    pacienteId: string
  ): MarcadorAutoanticorpo[] => {
    return marcadores
      .filter((m) => m.pacienteId === pacienteId)
      .sort(
        (a, b) =>
          new Date(b.dataColeta).getTime() - new Date(a.dataColeta).getTime()
      );
  };

  // ============================================
  // ✏️ EDITAR MARCADOR
  // ============================================
  const editarMarcador = async (
    id: string,
    dados: Partial<MarcadorAutoanticorpo>
  ) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Editando marcador:", id);

      const dadosFirebase: any = {
        editadoPor: usuarioLogado.id,
        editadoPorNome: usuarioLogado.nome,
        dataEdicao: serverTimestamp(),
      };

      // Adiciona apenas campos que foram alterados.
      // FIX (AI_99, achado do teste B213): as telas mandam o objeto
      // INTEIRO de volta, incluindo `criadoEm` convertido p/ string na
      // leitura (no banco é Timestamp) — a regra de imutabilidade do B81
      // via "valor diferente" e NEGAVA o update inteiro (a edição
      // revertia). Campos imutáveis/metadados ficam fora do payload.
      const CAMPOS_BLOQUEADOS = new Set([
        "id",
        "registradoPor",
        "registradoPorNome",
        "criadoEm",
        "dataEdicao",
        "editadoPor",
        "editadoPorNome",
        "pacienteId",
      ]);
      Object.keys(dados).forEach((key) => {
        const value = (dados as any)[key];
        if (value !== undefined && !CAMPOS_BLOQUEADOS.has(key)) {
          dadosFirebase[key] = value;
        }
      });

      await updateDoc(doc(db, "marcadores", id), dadosFirebase);

      // B213 — trilha de auditoria
      const atual = marcadores.find((m) => m.id === id);
      await registrarEdicaoTrilha({
        colecao: "marcadores",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: atual?.dataColeta
          ? `Coleta de ${new Date(atual.dataColeta).toLocaleDateString("pt-BR")}`
          : "Marcadores",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        camposAlterados: calcularCamposAlterados(dados, atual),
      });

      console.log("✅ Marcador editado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao editar marcador:", error);
      throw error;
    }
  };

  // ============================================
  // 🗑️ REMOVER MARCADOR
  // ============================================
  const removerMarcador = async (id: string) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("🗑️ Removendo marcador:", id);

      // B213 — captura antes do delete (a lápide sobrevive ao doc)
      const atual = marcadores.find((m) => m.id === id);

      await deleteDoc(doc(db, "marcadores", id));

      await registrarExclusaoTrilha({
        colecao: "marcadores",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: atual?.dataColeta
          ? `Coleta de ${new Date(atual.dataColeta).toLocaleDateString("pt-BR")}`
          : "Marcadores",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
      });

      console.log("✅ Marcador removido com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao remover marcador:", error);
      throw error;
    }
  };

  return (
    <MarcadoresContext.Provider
      value={{
        marcadores,
        loading,
        adicionarMarcador,
        listarMarcadoresPorPaciente,
        editarMarcador,
        removerMarcador,
        carregarMarcadores,
      }}
    >
      {children}
    </MarcadoresContext.Provider>
  );
}

export function useMarcadores() {
  const context = useContext(MarcadoresContext);
  if (!context) {
    throw new Error(
      "useMarcadores deve ser usado dentro de MarcadoresProvider"
    );
  }
  return context;
}
